import { useState, useEffect, useRef, useMemo } from 'react';
import { contactsAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { User, ChevronDown, X, Loader2 } from 'lucide-react';

/**
 * Elegir a **una persona que ya existe** en el directorio, escribiendo su nombre.
 *
 * **De dónde sale.** Diego, el 2026-09-02, tratando de cargar quién es el jefe de alguien:
 * *"es una lista desplegable compleja de usar porque estoy obligado a conocer exactamente cómo
 * está registrado el nombre, no prefiltra en base al texto ingresado"*. Con 233 personas, un
 * `<select>` nativo no se recorre: se abandona. Y lo único que ofrece para buscar es saltar por
 * prefijo exacto, o sea que hay que acertar cómo quedó escrito el nombre en el organigrama.
 *
 * **Por qué es un componente y no un arreglo en esa pantalla.** El mismo desplegable plano
 * estaba en tres lugares distintos —el jefe de una persona, el responsable de una gerencia, el
 * autorizante de un caso— cada uno resuelto por su lado, y arreglar sólo el que dolía dejaba los
 * otros dos esperando a que alguien más se quejara. La pregunta *«quién es esta persona»* es una
 * sola y merece una respuesta sola.
 *
 * **No es `ContactPicker`, y la diferencia es de fondo.** Aquél acepta texto libre y crea el
 * contacto: es lo correcto cuando llega un mail de alguien que todavía no existe. Acá, **no
 * existir es la respuesta equivocada**: un jefe inventado se guarda igual de bien que uno real, y
 * el fallo aparece semanas después, cuando esa persona abre su bandeja, la ve vacía y no tiene
 * forma de saber si es que no tiene gente a cargo o si el vínculo quedó roto.
 *
 * Con `opciones`, filtra sobre esa lista sin ir al servidor —sirve para el equipo de una cápsula,
 * que son once—; sin `opciones`, busca en el directorio entero.
 */
export default function SelectorDePersona({
  value,
  onChange,
  opciones = null,
  placeholder = 'Escribí un nombre…',
  sinElegir = '— sin elegir —',
  excluir = null,
  testId,
  className = '',
}) {
  //: **El campo tiene un solo texto y siempre muestra algo.** La primera versión tenía dos —el
  //: nombre elegido cuando estaba cerrado, el término de búsqueda cuando estaba abierto— y
  //: `onFocus` vaciaba el término. O sea: elegías a alguien, se veía bien, y **en cuanto volvías
  //: a hacer clic en el campo se vaciaba**. El nombre sobrevivía sólo como placeholder gris, que
  //: se lee igual que un campo vacío.
  //:
  //: Diego lo reportó dos veces como «se guarda pero no se muestra» y estaba guardado las dos
  //: veces. Es la peor forma del error: la pantalla dice que el trabajo no se hizo, así que se
  //: vuelve a hacer.
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [delServidor, setDelServidor] = useState([]);
  const [elegida, setElegida] = useState(null);
  const caja = useRef(null);
  //: **Cuál búsqueda es la última.** Sin esto las respuestas se pisan entre sí: al enfocar sale
  //: una con texto vacío y al tipear otra con «Lucas», y si la vacía vuelve segunda —que es lo
  //: normal, porque trae más filas— el desplegable termina mostrando el directorio entero
  //: mientras el campo dice «Lucas». Se ve como si el filtro no funcionara.
  //:
  //: Lo encontró la prueba con navegador del 2026-09-02, en la primera corrida.
  const ultimaBusqueda = useRef(0);

  const local = Array.isArray(opciones);

  // El nombre de lo que ya está elegido. Con `opciones` sale de la lista; sin ellas hay que
  // preguntarlo, porque el valor guardado es un id y la pantalla tiene que mostrar un nombre.
  //
  // **Se pide por id, no se busca por texto.** La primera versión llamaba al buscador pasándole
  // el `contact_id` como término, y el buscador mira nombre, apellido e identidades: no
  // encontraba nada, `elegida` quedaba en null y el campo se veía vacío. El responsable estaba
  // guardado y la pantalla decía que no había ninguno — que es lo que hace que alguien lo cargue
  // de nuevo, igual que un error que aparece después de una escritura que sí funcionó.
  useEffect(() => {
    if (!value) { setElegida(null); return; }
    if (local) {
      setElegida(opciones.find((p) => p.contact_id === value) || null);
      return;
    }
    let vivo = true;
    contactsAPI.get(value)
      .then(({ data }) => { if (vivo && data) setElegida(data); })
      .catch(() => {
        // La persona ya no está. Se deja en null **y no se limpia el valor guardado**: borrarlo
        // solo porque no se pudo leer convertiría una lectura fallida en una pérdida de dato.
        if (vivo) setElegida(null);
      });
    return () => { vivo = false; };
  }, [value, local, opciones]);

  // El texto sigue a quien está elegido. Es lo que hace que el campo nunca se vea vacío
  // teniendo algo guardado.
  useEffect(() => { setTexto(elegida?.display_name || ''); }, [elegida]);

  useEffect(() => {
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) {
        setAbierto(false);
        // **Una búsqueda a medias no puede quedar como si fuera el valor.** Si escribió «Luc» y
        // cerró sin elegir, el campo vuelve a decir quién está elegido de verdad.
        setTexto(elegida?.display_name || '');
      }
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [elegida]);

  const filtradas = useMemo(() => {
    if (!local) return delServidor;
    const q = texto.trim().toLowerCase();
    const base = opciones.filter((p) => p.contact_id !== excluir);
    if (!q) return base.slice(0, 50);
    return base.filter((p) => (p.display_name || '').toLowerCase().includes(q)).slice(0, 50);
  }, [local, opciones, texto, delServidor, excluir]);

  const buscar = async (q) => {
    if (local) return;
    const mia = ++ultimaBusqueda.current;
    setBuscando(true);
    try {
      const { data } = await contactsAPI.list({ q: q || undefined, limit: 20 });
      // Llegó tarde: ya se pidió otra cosa. Se descarta en vez de pisar lo que se está mirando.
      if (mia !== ultimaBusqueda.current) return;
      setDelServidor((data || []).filter((p) => p.contact_id !== excluir));
    } catch {
      if (mia === ultimaBusqueda.current) setDelServidor([]);
    } finally {
      if (mia === ultimaBusqueda.current) setBuscando(false);
    }
  };

  const elegir = (p) => {
    setElegida(p);
    setAbierto(false);
    onChange?.(p ? p.contact_id : '', p || null);
  };

  return (
    <div className={`relative ${className}`} ref={caja}>
      <div className="relative">
        <User className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
        <Input
          data-testid={testId}
          value={texto}
          placeholder={value && !elegida
            ? 'hay alguien elegido y no se pudo leer su nombre'
            : sinElegir}
          // **Se selecciona en vez de borrarse.** Escribir lo reemplaza igual —que es lo que se
          // quiere al cambiar de persona— pero mirar el campo no lo vacía.
          onFocus={(e) => { setAbierto(true); e.target.select(); buscar(''); }}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); buscar(e.target.value); }}
          className="pl-7 pr-12 h-8 text-xs rounded-sm"
        />
        {!!value && (
          <button
            type="button"
            data-testid={testId ? `${testId}-limpiar` : undefined}
            title="Sacar a esta persona"
            onClick={() => elegir(null)}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-red-500"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400 pointer-events-none" />
      </div>

      {abierto && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-brand-border rounded-sm shadow-lg max-h-56 overflow-auto">
          {buscando && (
            <div className="p-2 text-[10px] text-zinc-400 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Buscando…
            </div>
          )}
          {!buscando && !filtradas.length && (
            /* **Decir que no hay, y no dejar el desplegable vacío.** Una lista vacía se lee como
               «se está cargando» y quien busca se queda esperando. */
            <div className="p-2 text-[10px] text-zinc-500">
              {texto
                ? <>Nadie que se llame «{texto}». {local ? 'Es la gente de este equipo.' : 'Se busca en el directorio de la organización.'}</>
                : 'Escribí un nombre para buscar.'}
            </div>
          )}
          {filtradas.map((p) => (
            <button
              key={p.contact_id}
              type="button"
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
              onClick={() => elegir(p)}
            >
              <span className="font-medium">{p.display_name}</span>
              {p.org_profile?.job_title && (
                <span className="ml-1.5 text-[10px] text-zinc-500">{p.org_profile.job_title}</span>
              )}
              {p.org_profile?.department && (
                <span className="ml-1.5 text-[10px] text-zinc-400">· {p.org_profile.department}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
